/* assets/brochure-lifecycle-explorer.js — GPROJECT-LIFECYCLE-EXPLORER-U02
 * Single owner: stage navigation/progressive enhancement for the seven existing brochure panels.
 * The HTML panels remain fully readable until all seven navigation controls initialize.
 * No project content, commerce state, timeline progress or page-wide visual owner here.
 */
(function (window, document) {
  'use strict';

  if (window.__GLG_BROCHURE_LIFECYCLE_EXPLORER_U02__) return;
  window.__GLG_BROCHURE_LIFECYCLE_EXPLORER_U02__ = true;

  var ROOT_SELECTOR = '[data-glg-lifecycle-explorer]';
  var STAGES = [
    'project-planning',
    'pre-project-preparation',
    'project-approval',
    'implementation-monitoring',
    'operations-governance',
    'productization-promotion',
    'data-value-realization'
  ];
  var instances = new WeakMap();

  function makeText(tag, className, textValue) {
    var node = document.createElement(tag);
    node.className = className;
    node.textContent = textValue;
    return node;
  }

  function mount(root) {
    if (!root || instances.has(root) || root.dataset.glgLifecycleReady === 'true') return;

    var nav = root.querySelector('[data-glg-lifecycle-nav]');
    var grid = root.querySelector('.b2s-project-grid--lifecycle');
    var pagination = root.querySelector('[data-glg-lifecycle-pagination]');
    var imageNote = root.querySelector('[data-glg-lifecycle-image-note]');
    var count = root.querySelector('[data-glg-lifecycle-count]');
    var previous = root.querySelector('[data-glg-lifecycle-prev]');
    var next = root.querySelector('[data-glg-lifecycle-next]');
    if (!nav || !grid || !pagination || !imageNote || !count || !previous || !next) return;

    var panels = Array.prototype.filter.call(grid.children, function (node) {
      return node.matches('[data-glg-lifecycle-panel][data-lifecycle-stage]');
    });
    if (panels.length !== STAGES.length || grid.children.length !== STAGES.length || nav.children.length) return;

    var placeholderSrc = root.getAttribute('data-glg-lifecycle-placeholder-src');
    if (!placeholderSrc) return;

    var info = [];
    for (var i = 0; i < STAGES.length; i += 1) {
      var panel = panels[i];
      var label = panel.querySelector('.b2s-project-card__topline p');
      var number = panel.querySelector('.b2s-project-card__topline strong');
      var summary = panel.querySelector('h3');
      if (!label || !number || !summary ||
          panel.getAttribute('data-lifecycle-stage') !== STAGES[i] ||
          panel.getAttribute('data-glg-lifecycle-panel') !== STAGES[i] ||
          panel.id !== 'glg-lifecycle-panel-' + STAGES[i]) return;
      info.push({
        label: label.textContent.replace(/\s+/g, ' ').trim(),
        number: number.textContent.replace(/\s+/g, ' ').trim(),
        summary: summary.textContent.replace(/\s+/g, ' ').trim()
      });
    }

    const controller = new AbortController();
    var buttons = [];
    var activeIndex = 0;

    let stInstance = null;
    let navigationTween = null;
    let isNavigating = false;
    let pinRefreshFrame = 0;

    function applyPinProgress(self, allowOutside = false) {
      if (
        controller.signal.aborted ||
        isNavigating ||
        (!allowOutside && !self.isActive) ||
        document.querySelector('dialog[open]') ||
        panels[activeIndex].contains(document.activeElement)
      ) return;

      const index = Math.min(
        STAGES.length - 1,
        Math.max(0, Math.floor(self.progress * STAGES.length))
      );

      if (index !== activeIndex) activate(index, false, true);
    }

    function schedulePinRefresh() {
      if (
        controller.signal.aborted ||
        !stInstance ||
        pinRefreshFrame
      ) return;

      pinRefreshFrame = window.requestAnimationFrame(function () {
        pinRefreshFrame = 0;
        if (controller.signal.aborted || !stInstance) return;
        stInstance.refresh();
      });
    }

    const pinMedia = window.matchMedia(
      '(min-width: 1024px) and (min-height: 720px) ' +
      'and (hover: hover) and (pointer: fine) ' +
      'and (prefers-reduced-motion: no-preference)'
    );

    function getPinOffset() {
      const value = parseFloat(
        window.getComputedStyle(root).getPropertyValue('--header-height')
      );
      return (Number.isFinite(value) ? Math.max(0, value) : 56) + 16;
    }

    function updatePinHeight() {
      root.style.setProperty(
        '--glg-lifecycle-pin-height',
        Math.max(320, window.innerHeight - getPinOffset() - 24) + 'px'
      );
    }

    function cancelPinNavigation() {
      if (navigationTween) navigationTween.kill();
      navigationTween = null;
      isNavigating = false;
    }

    function destroyPin() {
      cancelPinNavigation();

      if (pinRefreshFrame) {
        window.cancelAnimationFrame(pinRefreshFrame);
        pinRefreshFrame = 0;
      }

      if (stInstance) {
        stInstance.kill(true);
        stInstance = null;
      }
      delete root.dataset.glgLifecyclePin;
      root.style.removeProperty('--glg-lifecycle-pin-height');
    }

    function syncPinToStage(index) {
      if (!stInstance || !Number.isInteger(index)) return;

      cancelPinNavigation();

      const target =
        stInstance.start +
        ((index + 0.5) / STAGES.length) *
        (stInstance.end - stInstance.start);

      isNavigating = true;
      navigationTween = window.gsap.to(window, {
        scrollTo: {
          y: target,
          autoKill: true,
          onAutoKill: cancelPinNavigation
        },
        duration: 0.45,
        ease: 'power2.out',
        onComplete: function () {
          navigationTween = null;
          isNavigating = false;
        },
        onInterrupt: function () {
          navigationTween = null;
          isNavigating = false;
        }
      });
    }

    function configurePin() {
      destroyPin();

      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;
      const ScrollToPlugin = window.ScrollToPlugin;

      if (
        controller.signal.aborted ||
        !pinMedia.matches ||
        !gsap ||
        !ScrollTrigger ||
        !ScrollToPlugin ||
        root.dataset.glgLifecycleReady !== 'true'
      ) return;

      try {
        gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
        updatePinHeight();
        root.dataset.glgLifecyclePin = 'true';

        stInstance = ScrollTrigger.create({
          trigger: root,
          start: function () {
            return 'top ' + getPinOffset() + 'px';
          },
          end: function () {
            return '+=' + Math.round(
              STAGES.length * window.innerHeight * 0.65
            );
          },
          pin: root,
          pinType: 'transform',
          pinReparent: false,
          pinSpacing: true,
          invalidateOnRefresh: true,
          onRefreshInit: updatePinHeight,
          onRefresh: function (self) {
            applyPinProgress(self);
          },
          onEnter: function (self) {
            applyPinProgress(self);
          },
          onEnterBack: function (self) {
            applyPinProgress(self);
          },
          onLeave: function (self) {
            applyPinProgress(self, true);
          },
          onLeaveBack: function (self) {
            applyPinProgress(self, true);
          },
          onUpdate: function (self) {
            applyPinProgress(self);
          }
        });
      } catch (error) {
        destroyPin();
        if (window.console && window.console.warn) {
          window.console.warn('GProject lifecycle pin fallback:', error);
        }
      }
    }

    function cleanup() {
      controller.abort();
      destroyPin();
      panels.forEach(function (panel) {
        panel.hidden = false;
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
      });
      nav.replaceChildren();
      pagination.hidden = true;
      imageNote.hidden = true;
      root.dataset.glgLifecycleReady = 'false';
      instances.delete(root);
    }

    function scrollToButton(button, silent = false) {
      if (
        controller.signal.aborted ||
        !button?.isConnected ||
        typeof nav.scrollTo !== 'function'
      ) return;

      const reduceMotion = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = reduceMotion || silent ? 'auto' : 'smooth';
      const navRect = nav.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const inset = 6;

      if (nav.scrollWidth > nav.clientWidth) {
        let delta = 0;

        if (buttonRect.left < navRect.left + inset) {
          delta = buttonRect.left - navRect.left - inset;
        } else if (buttonRect.right > navRect.right - inset) {
          delta = buttonRect.right - navRect.right + inset;
        }

        if (!delta) return;

        nav.scrollTo({
          left: Math.max(
            0,
            Math.min(
              nav.scrollWidth - nav.clientWidth,
              nav.scrollLeft + delta
            )
          ),
          behavior
        });
      } else if (nav.scrollHeight > nav.clientHeight) {
        let delta = 0;

        if (buttonRect.top < navRect.top + inset) {
          delta = buttonRect.top - navRect.top - inset;
        } else if (buttonRect.bottom > navRect.bottom - inset) {
          delta = buttonRect.bottom - navRect.bottom + inset;
        }

        if (!delta) return;

        nav.scrollTo({
          top: Math.max(
            0,
            Math.min(
              nav.scrollHeight - nav.clientHeight,
              nav.scrollTop + delta
            )
          ),
          behavior
        });
      }
    }

    function activate(index, focusButton, fromScroll = false) {
      if (
        controller.signal.aborted ||
        !root.isConnected ||
        !Number.isInteger(index) ||
        index < 0 ||
        index >= STAGES.length ||
        !buttons[index] ||
        !panels[index]
      ) return;
      const focusWasInPanel = panels.some(function (panel) {
        return panel.contains(document.activeElement);
      });

      activeIndex = index;
      panels.forEach(function (panel, position) {
        panel.hidden = position !== index;
        buttons[position].setAttribute('aria-expanded', position === index ? 'true' : 'false');
        if (position === index) {
          buttons[position].setAttribute('aria-current', 'step');
        } else {
          buttons[position].removeAttribute('aria-current');
        }
      });
      count.setAttribute('aria-live', fromScroll ? 'off' : 'polite');
      count.textContent =
        String(index + 1).padStart(2, '0') +
        ' / 07 · ' +
        info[index].label;
      previous.disabled = index === 0;
      next.disabled = index === STAGES.length - 1;
      if (focusButton || focusWasInPanel) {
        buttons[index].focus({ preventScroll: true });
      }
      scrollToButton(buttons[index], fromScroll);
      if (!fromScroll) syncPinToStage(index);
    }

    function indexFromHash() {
      var hash = window.location && window.location.hash ? window.location.hash.slice(1) : '';
      if (!hash) return -1;
      try { hash = decodeURIComponent(hash); } catch (_error) { return -1; }
      return panels.findIndex(function (panel, i) {
        return panel.id === hash || buttons[i].id === hash;
      });
    }

    function showHashStage() {
      var index = indexFromHash();
      if (index < 0) return;
      activate(index, false);
      if (!stInstance && typeof panels[index].scrollIntoView === 'function') {
        panels[index].scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }

    try {
      var fragment = document.createDocumentFragment();
      info.forEach(function (stage, index) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'b2s-lifecycle-explorer__trigger';
        button.id = 'glg-lifecycle-trigger-' + STAGES[index];
        button.setAttribute('data-glg-lifecycle-trigger', STAGES[index]);
        button.setAttribute('aria-controls', panels[index].id);
        button.setAttribute('aria-expanded', index === 0 ? 'true' : 'false');
        button.setAttribute(
          'aria-label',
          stage.number + ' / 07 · ' + stage.label
        );

        var media = makeText('span', 'b2s-lifecycle-explorer__media', '');
        var picture = document.createElement('img');
        picture.className = 'b2s-lifecycle-explorer__image';
        picture.src = panels[index].getAttribute('data-glg-lifecycle-image-src') || placeholderSrc;
        picture.alt = panels[index].getAttribute('data-glg-lifecycle-image-alt') || '';
        picture.width = 640;
        picture.height = 360;
        picture.loading = 'lazy';
        picture.decoding = 'async';
        media.appendChild(picture);
        if (!panels[index].getAttribute('data-glg-lifecycle-image-src')) {
          var badge = makeText('span', 'b2s-lifecycle-explorer__placeholder-badge', imageNote.textContent.replace(/\s+/g, ' ').trim());
          badge.setAttribute('aria-hidden', 'true');
          media.appendChild(badge);
        }

        var body = makeText('span', 'b2s-lifecycle-explorer__preview', '');
        body.appendChild(makeText('span', 'b2s-lifecycle-explorer__number', stage.number + ' / 07'));
        body.appendChild(makeText('span', 'b2s-lifecycle-explorer__title', stage.label));
        body.appendChild(makeText('span', 'b2s-lifecycle-explorer__summary', stage.summary));
        button.appendChild(media);
        button.appendChild(body);
        fragment.appendChild(button);
        buttons.push(button);
      });

      nav.appendChild(fragment);
      panels.forEach(function (panel, index) {
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', buttons[index].id);
      });

      nav.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-glg-lifecycle-trigger]');
        if (!trigger || !nav.contains(trigger)) return;
        activate(STAGES.indexOf(trigger.getAttribute('data-glg-lifecycle-trigger')), false);
      }, { signal: controller.signal });

      nav.addEventListener('keydown', function (event) {
        var trigger = event.target.closest('[data-glg-lifecycle-trigger]');
        if (!trigger || !nav.contains(trigger)) return;
        const index = STAGES.indexOf(
          trigger.getAttribute('data-glg-lifecycle-trigger')
        );
        if (index < 0) return;

        const isVertical = window.matchMedia('(min-width: 1024px)').matches;
        const forwardKey = isVertical ? 'ArrowDown' : 'ArrowRight';
        const backwardKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
        let nextIndex = index;

        if (event.key === forwardKey) {
          nextIndex = Math.min(index + 1, STAGES.length - 1);
        } else if (event.key === backwardKey) {
          nextIndex = Math.max(index - 1, 0);
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = STAGES.length - 1;
        } else {
          return;
        }
        event.preventDefault();
        activate(nextIndex, true);
      }, { signal: controller.signal });

      previous.addEventListener('click', function () {
        activate(activeIndex - 1, true);
      }, { signal: controller.signal });
      window.addEventListener('hashchange', showHashStage, { signal: controller.signal });
      next.addEventListener('click', function () {
        activate(activeIndex + 1, true);
      }, { signal: controller.signal });

      document.addEventListener('shopify:section:unload', function (event) {
        if (event.target && (event.target === root || event.target.contains(root))) cleanup();
      }, { signal: controller.signal });

      var initialIndex = indexFromHash();
      activate(initialIndex < 0 ? 0 : initialIndex, false);
      pagination.hidden = false;
      imageNote.hidden = true;
      root.dataset.glgLifecycleReady = 'true';
      instances.set(root, cleanup);

      if (typeof pinMedia.addEventListener === 'function') {
        pinMedia.addEventListener('change', configurePin, {
          signal: controller.signal
        });

        window.addEventListener('wheel', cancelPinNavigation, {
          passive: true,
          signal: controller.signal
        });
        window.addEventListener('touchstart', cancelPinNavigation, {
          passive: true,
          signal: controller.signal
        });

        window.addEventListener('pointerdown', cancelPinNavigation, {
          passive: true,
          signal: controller.signal
        });

        window.addEventListener('keydown', function (event) {
          const target = event.target;

          if (
            target instanceof window.Element &&
            root.contains(target) &&
            target.closest(
              '[data-glg-lifecycle-trigger], ' +
              '[data-glg-lifecycle-prev], ' +
              '[data-glg-lifecycle-next]'
            )
          ) return;

          if ([
            'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown',
            'Home', 'End', ' ', 'Escape'
          ].includes(event.key)) {
            cancelPinNavigation();
          }
        }, { signal: controller.signal });

        root.addEventListener('toggle', function (event) {
          if (
            event.target instanceof window.Element &&
            event.target.matches('details')
          ) {
            schedulePinRefresh();
          }
        }, {
          capture: true,
          signal: controller.signal
        });

        root.addEventListener('load', function (event) {
          if (
            event.target instanceof window.Element &&
            event.target.matches('img')
          ) {
            schedulePinRefresh();
          }
        }, {
          capture: true,
          signal: controller.signal
        });

        window.addEventListener('load', schedulePinRefresh, {
          once: true,
          signal: controller.signal
        });

        configurePin();

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function () {
            if (!controller.signal.aborted) schedulePinRefresh();
          });
        }
      }

      if (initialIndex >= 0) {
        if (stInstance) {
          syncPinToStage(initialIndex);
        } else if (typeof panels[initialIndex].scrollIntoView === 'function') {
          panels[initialIndex].scrollIntoView({
            behavior: 'auto',
            block: 'start'
          });
        }
      }
    } catch (error) {
      cleanup();
      if (window.console && window.console.error) window.console.error('GProject lifecycle fallback:', error);
    }
  }

  function mountAll(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR)) mount(scope);
    if (scope.querySelectorAll) {
      Array.prototype.forEach.call(scope.querySelectorAll(ROOT_SELECTOR), mount);
    }
  }

  const previousBoot = window.__GLG_BROCHURE_LIFECYCLE_BOOT__;
  if (previousBoot) {
    document.removeEventListener('shopify:section:load', previousBoot);
    document.removeEventListener('DOMContentLoaded', previousBoot);
  }

  const boot = function (event) {
    const scope = event && event.type === 'shopify:section:load'
      ? event.target
      : document;
    mountAll(scope);
  };

  window.__GLG_BROCHURE_LIFECYCLE_BOOT__ = boot;
  document.addEventListener('shopify:section:load', boot);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})(window, document);
